import { supabase } from './supabase'

export type StructureType = 'single_hbl' | 'manifest' | 'multi_sheet_excel' | 'single_page_multi_hbl'

// Z58.24: per-structure_type parsing hints, set by the template's structure-type
// step in DocumentTemplatesPage. All optional -- each structure type's dispatch
// falls back to its existing (pre-Z58.24) default detection when unset.
export interface StructureConfig {
  // manifest
  header_marker?: string
  block_separator?: string
  hbl_count_field?: string
  // multi_sheet_excel
  sheet_filter?: string
  skip_sheets?: string[]
  hbl_ref_cell?: string
  // single_page_multi_hbl
  hbl_delimiter_pattern?: string
  hbl_ref_pattern?: string
}

export interface DocumentTemplate {
  id?: string
  template_type: 'MAWB' | 'HAWB' | 'MBL' | 'HBL'
  carrier_agent_name: string
  detection_keywords: string[]
  transport_mode: 'AIR' | 'SEA' | 'ROAD'
  direction: 'IMP' | 'EXP' | 'BOTH'
  field_hints?: string
  // Z58.24: customer identity -- lets a template be matched by which customer's
  // documents it describes, not just document layout/keywords. customer_name is
  // denormalized (same pattern as invoice_templates) for display + text matching
  // without a join. Both null for every pre-Z58.24 template (see "Legacy
  // template" badge in the Templates list).
  customer_id?: string | null
  customer_name?: string | null
  // Z58.24: which of the 4 known HBL file shapes this template describes.
  // Defaults to 'single_hbl' (the only shape that existed before this).
  structure_type?: StructureType
  structure_config?: StructureConfig
  usage_count?: number
  created_by?: string
  created_at?: string
  updated_at?: string
}

export async function getTemplates(templateType?: string): Promise<DocumentTemplate[]> {
  try {
    let query = supabase.from('document_templates').select('*')

    if (templateType) {
      query = query.eq('template_type', templateType)
    }

    const { data, error } = await query.order('usage_count', { ascending: false })

    if (error) throw error
    return data || []
  } catch (err) {
    console.error('[templateManager] Error fetching templates:', err)
    throw err
  }
}

export async function createTemplate(template: DocumentTemplate): Promise<DocumentTemplate> {
  try {
    const { data, error } = await supabase
      .from('document_templates')
      .insert([template])
      .select()
      .single()

    if (error) throw error
    return data
  } catch (err) {
    console.error('[templateManager] Error creating template:', err)
    throw err
  }
}

export async function updateTemplate(id: string, updates: Partial<DocumentTemplate>): Promise<DocumentTemplate> {
  try {
    const { data, error } = await supabase
      .from('document_templates')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (err) {
    console.error('[templateManager] Error updating template:', err)
    throw err
  }
}

export async function deleteTemplate(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('document_templates')
      .delete()
      .eq('id', id)

    if (error) throw error
  } catch (err) {
    console.error('[templateManager] Error deleting template:', err)
    throw err
  }
}

export async function incrementUsageCount(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('document_templates')
      .update({ usage_count: supabase.rpc('increment', { id }) })
      .eq('id', id)

    if (error) console.error('[templateManager] Error incrementing usage:', error)
  } catch (err) {
    console.error('[templateManager] Error with usage count:', err)
  }
}

// Match if at least 50% of a template's keywords are found, or at least 2 keywords.
function matchByKeywords(candidates: DocumentTemplate[], lowerText: string): DocumentTemplate | null {
  for (const template of candidates) {
    const keywordsFound = template.detection_keywords.filter(keyword =>
      lowerText.includes(keyword.toLowerCase())
    )
    const matchThreshold = Math.max(2, Math.ceil(template.detection_keywords.length * 0.5))
    if (keywordsFound.length >= matchThreshold) {
      return template
    }
  }
  return null
}

export function detectTemplate(
  text: string,
  templateType?: string,
  templates?: DocumentTemplate[]
): DocumentTemplate | null {
  if (!templates || templates.length === 0) return null

  const lowerText = text.toLowerCase()

  // Filter by template type if specified
  let candidates = templates
  if (templateType) {
    candidates = candidates.filter(t => t.template_type === templateType)
  }

  return matchByKeywords(candidates, lowerText)
}

// Z58.24: prefer a template linked to the customer whose name appears in the
// document text over one that only matches on document-layout keywords -- two
// customers with similar layouts no longer collide on keyword overlap alone
// (the original problem this ticket was raised for). Falls through to the
// existing keyword-only matching when no customer-linked template's name
// appears in the text -- covers every pre-Z58.24 template, which has none.
export function matchTemplateByCustomerOrKeywords(
  templates: DocumentTemplate[] | undefined,
  text: string,
  templateType?: string
): DocumentTemplate | null {
  if (!templates || templates.length === 0) return null

  const lowerText = text.toLowerCase()
  let candidates = templates
  if (templateType) {
    candidates = candidates.filter(t => t.template_type === templateType)
  }

  const customerMatches = candidates.filter(t =>
    t.customer_id && t.customer_name?.trim() && lowerText.includes(t.customer_name.trim().toLowerCase())
  )
  if (customerMatches.length === 1) return customerMatches[0]
  if (customerMatches.length > 1) {
    // Ambiguous on customer name alone (e.g. two templates for the same
    // customer, different structure types) -- narrow by keyword match among
    // just the customer-linked candidates rather than widening the search.
    const narrowed = matchByKeywords(customerMatches, lowerText)
    if (narrowed) return narrowed
  }

  return matchByKeywords(candidates, lowerText)
}
